import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as merchantHeatmapApi from './MerchantHeatmap.api';
import { MerchantHeatmapUIState, mapApiResponseToUI, MerchantHeatmapFilters, LocationAnalyticsData } from './MerchantHeatmap.data';
import { MERCHANT_HEATMAP_NOTIFICATION_MESSAGES } from './MerchantHeatmap.const';

interface MerchantHeatmapState {
    data: MerchantHeatmapUIState | null;
    isLoading: boolean;
    isLoadingLocationAnalytics: boolean;
    error: string | null;
    tempLocationAnalytics: LocationAnalyticsData | null;
}

const initialState: MerchantHeatmapState = {
    data: null,
    isLoading: false,
    isLoadingLocationAnalytics: false,
    tempLocationAnalytics: null,
    error: null,
};

export const fetchMerchantHeatmapData = createAsyncThunk(
    'merchantHeatmap/fetchData',
    async (filters: MerchantHeatmapFilters | undefined = undefined, { rejectWithValue }) => {
        try {
            const response = await merchantHeatmapApi.fetchMerchantHeatmapData(filters);
            return { response, filters: filters || {} };
        } catch (error: any) {
            return rejectWithValue(error.message || MERCHANT_HEATMAP_NOTIFICATION_MESSAGES.FETCH_ERROR);
        }
    }
);

export const fetchLocationAnalytics = createAsyncThunk(
    'merchantHeatmap/fetchLocationAnalytics',
    async (_, { rejectWithValue }) => {
        try {
            const response = await merchantHeatmapApi.fetchLocationAnalytics();
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to load location analytics');
        }
    }
);

export const exportMerchantHeatmapCSV = createAsyncThunk(
    'merchantHeatmap/exportCSV',
    async (filters: MerchantHeatmapFilters | undefined = undefined, { rejectWithValue }) => {
        try {
            const blob = await merchantHeatmapApi.exportMerchantHeatmapCSV(filters);
            return blob;
        } catch (error: any) {
            return rejectWithValue(error.message || MERCHANT_HEATMAP_NOTIFICATION_MESSAGES.EXPORT_ERROR);
        }
    }
);

export const exportMerchantHeatmapXLSX = createAsyncThunk(
    'merchantHeatmap/exportXLSX',
    async (filters: MerchantHeatmapFilters | undefined = undefined, { rejectWithValue }) => {
        try {
            const blob = await merchantHeatmapApi.exportMerchantHeatmapXLSX(filters);
            return blob;
        } catch (error: any) {
            return rejectWithValue(error.message || MERCHANT_HEATMAP_NOTIFICATION_MESSAGES.EXPORT_ERROR);
        }
    }
);

const merchantHeatmapSlice = createSlice({
    name: 'merchantHeatmap',
    initialState,
    reducers: {
        setSelectedLocation: (state, action) => {
            if (state.data) {
                state.data.selectedLocation = action.payload;
            }
        },
        clearSelectedLocation: (state) => {
            if (state.data) {
                state.data.selectedLocation = null;
            }
        },
        updateFilters: (state, action) => {
            if (state.data) {
                state.data.filters = { ...state.data.filters, ...action.payload };
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchMerchantHeatmapData.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchMerchantHeatmapData.fulfilled, (state, action) => {
                state.isLoading = false;
                const newData = mapApiResponseToUI(action.payload.response, action.payload.filters);
                // Preserve location_analytics if it was already loaded
                if (state.data?.location_analytics) {
                    newData.location_analytics = state.data.location_analytics;
                } else if (state.tempLocationAnalytics) {
                    // Use temporary location_analytics if main data loads after location_analytics
                    newData.location_analytics = state.tempLocationAnalytics;
                    state.tempLocationAnalytics = null;
                }
                state.data = newData;
            })
            .addCase(fetchMerchantHeatmapData.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            .addCase(fetchLocationAnalytics.pending, (state) => {
                state.isLoadingLocationAnalytics = true;
            })
            .addCase(fetchLocationAnalytics.fulfilled, (state, action) => {
                state.isLoadingLocationAnalytics = false;
                if (state.data) {
                    state.data.location_analytics = action.payload;
                } else {
                    // If main data hasn't loaded yet, store location_analytics temporarily
                    // It will be merged when fetchMerchantHeatmapData completes
                    state.tempLocationAnalytics = action.payload;
                }
            })
            .addCase(fetchLocationAnalytics.rejected, (state, action) => {
                state.isLoadingLocationAnalytics = false;
                state.error = action.payload as string;
            });
    },
});

export const { setSelectedLocation, clearSelectedLocation, updateFilters } = merchantHeatmapSlice.actions;
export default merchantHeatmapSlice.reducer;

